import classNames from 'classnames';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { getWidthStyle } from '../utils/sizing';
import * as styles from './Accordion.css';
import type { AccordionContentElementProps } from './Accordion.types';

const DEFAULTS = {
  padding: '16px 0px 16px 0px',
};

export function AccordionContentElement(props: AccordionContentElementProps) {
  const { element, children, attributes, className } = props;
  const { width, width_max, mobile_hide, desktop_hide, padding, customAttributes } = element.attrs || {};

  const [contentHeight, setContentHeight] = useState<number | string>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const getVerticalPadding = () => {
    const paddingValue = padding || DEFAULTS.padding;
    if (typeof paddingValue === 'number') {
      return paddingValue;
    }
    const parts = String(paddingValue).trim().split(/\s+/);
    // CSS padding can be:
    // 1 value: all sides
    // 2 values: vertical horizontal
    // 3 values: top horizontal bottom
    // 4 values: top right bottom left
    switch (parts.length) {
      case 1:
        return Number.parseInt(parts[0]); // Same padding all around
      case 2:
        return Number.parseInt(parts[0]); // Vertical padding
      case 3:
        return Number.parseInt(parts[0]) + Number.parseInt(parts[2]); // Top and bottom padding
      case 4:
        return Number.parseInt(parts[0]) + Number.parseInt(parts[2]); // Top and bottom padding
      default:
        return Number.parseInt(DEFAULTS.padding.split(' ')[0]);
    }
  };

  // Observe content height changes
  useEffect(() => {
    if (!contentRef.current) return;

    const updateHeight = () => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.scrollHeight + getVerticalPadding());
      }
    };
    const mutationObserver = new MutationObserver(updateHeight);

    mutationObserver.observe(contentRef.current, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });

    updateHeight();

    return () => {
      mutationObserver.disconnect();
    };
  }, []);

  return (
    <div
      {...customAttributes}
      ref={contentRef}
      className={classNames(
        className,
        {
          'mobile:hidden': mobile_hide,
          'desktop:hidden': desktop_hide,
        },
        styles.accordionContent,
        customAttributes?.className,
      )}
      style={
        {
          '--width': getWidthStyle(width, width_max).width,
          '--max-width': getWidthStyle(width, width_max).maxWidth,
          '--padding': padding ?? DEFAULTS.padding,
          '--content-height': `${contentHeight}px`,
        } as CSSProperties
      }
      {...attributes}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {children}
    </div>
  );
}
